import { authFetch, type AuthFetchOptions } from '../utils/authFetch';
import { ApiError, getApiErrorKind, parseJsonSafe, responseApiError } from './response';

export { ApiError, getApiErrorKind } from './response';
export type { ApiErrorKind, ApiFieldError } from './response';

export const fetchPublicApiResponse: typeof fetch = (...args) => fetch(...args);

export const fetchAuthedApiResponse: typeof authFetch = (...args) => {
  const delegate = authFetch as typeof authFetch;
  return delegate(...args);
};

/** Existing authenticated transport, with typed JSON/errors and an opt-in deadline.
 * No retries or UI side effects; each caller retains its query/mutation policy. */
export async function requestAuthedJson<T>(input: RequestInfo | URL, fallback: string | ((status?: number) => string),
  init: AuthFetchOptions = {}, options: { timeoutMs?: number; emptyValue?: T } = {}): Promise<T> {
  const message = (status?: number) => typeof fallback === 'string' ? fallback : fallback(status);
  const context = { method: (init.method ?? (input instanceof Request ? input.method : 'GET')).toUpperCase() };
  if (options.timeoutMs !== undefined && (!Number.isFinite(options.timeoutMs) || options.timeoutMs <= 0)) {
    throw new RangeError('timeoutMs must be a positive finite number');
  }
  const controller = init.signal || options.timeoutMs ? new AbortController() : undefined;
  const cancel = () => controller?.abort(init.signal?.reason);
  let timedOut = false;
  const timer = options.timeoutMs && options.timeoutMs > 0 ? setTimeout(() => {
    timedOut = true; controller?.abort(new DOMException('Request timed out', 'TimeoutError'));
  }, options.timeoutMs) : undefined;
  init.signal?.addEventListener('abort', cancel, { once: true });
  if (init.signal?.aborted) cancel();
  let aborted: (() => void) | undefined;
  const cancellation = controller ? new Promise<never>((_, reject) => {
    aborted = () => reject(controller.signal.reason ?? new DOMException('Request cancelled', 'AbortError'));
    controller.signal.addEventListener('abort', aborted, { once: true });
    if (controller.signal.aborted) aborted();
  }) : undefined;
  const request = async () => {
    controller?.signal.throwIfAborted();
    const response = await fetchAuthedApiResponse(input, controller ? { ...init, signal: controller.signal } : init);
    if (!response.ok) throw responseApiError(response, await parseJsonSafe(response), message(response.status), context);
    if ([204, 205].includes(response.status) && 'emptyValue' in options) return options.emptyValue as T;
    try { return await response.json() as T; }
    catch (error) {
      if (getApiErrorKind(error)) throw error;
      if (!(error instanceof SyntaxError)) throw error;
      throw new ApiError(message(response.status), { kind: 'invalid_response', status: response.status, context, cause: error });
    }
  };
  try { return await (cancellation ? Promise.race([request(), cancellation]) : request()); }
  catch (error) {
    if (timedOut) throw new ApiError(message(), { kind: 'timeout', context, cause: error });
    if (controller?.signal.aborted) throw controller.signal.reason ?? new DOMException('Request cancelled', 'AbortError');
    if (getApiErrorKind(error)) throw error;
    throw new ApiError(message(), { kind: 'network', context, cause: error });
  } finally {
    clearTimeout(timer); init.signal?.removeEventListener('abort', cancel);
    if (aborted) controller?.signal.removeEventListener('abort', aborted);
  }
}
